import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight, X, Maximize2, Minimize2, Volume2, VolumeX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  concierge: 'Concierge Driven',
  group: 'Group Driven',
  exotic: 'Exotic Driven',
};

const VirtualTour = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isKiosk = searchParams.get('kiosk') === '1';

  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [showControls, setShowControls] = useState(!isKiosk);
  const [kioskMode, setKioskMode] = useState(isKiosk);
  const [muted, setMuted] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const controlsTimerRef = useRef(null);

  // Fetch all fleet media and build slide deck
  useEffect(() => {
    fetch(`${API}/api/fleet-media/all`)
      .then(r => r.ok ? r.json() : { vehicles: [] })
      .then(data => {
        const deck = [];
        (data.vehicles || []).forEach(v => {
          const cat = v.category || 'concierge';
          const name = v.vehicle_name || v.vehicle_slug || '';
          (v.images || []).forEach(img => {
            deck.push({ type: 'image', url: img.url, name, category: cat, slug: v.vehicle_slug });
          });
          (v.videos || []).forEach(vid => {
            deck.push({ type: 'video', url: vid.url, name, category: cat, slug: v.vehicle_slug });
          });
        });
        if (deck.length === 0) {
          // Fallback with placeholder
          deck.push({ type: 'placeholder', name: 'No media uploaded yet', category: '' });
        }
        setSlides(deck);
      })
      .catch(() => {
        setSlides([{ type: 'placeholder', name: 'Unable to load media', category: '' }]);
      });
  }, []);

  const slide = slides[current];
  const total = slides.length;

  // Auto-advance logic
  const advance = useCallback(() => {
    if (!playing || total <= 1) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(prev => (prev + 1) % total);
      setTransitioning(false);
    }, 600);
  }, [playing, total]);

  useEffect(() => {
    if (!playing || total <= 1) return;
    // Videos auto-advance on end, images advance on timer
    if (slide?.type !== 'video') {
      timerRef.current = setTimeout(advance, 5500);
    }
    return () => clearTimeout(timerRef.current);
  }, [current, playing, slide, advance, total]);

  // Video ended handler
  const handleVideoEnd = useCallback(() => {
    if (playing) advance();
  }, [playing, advance]);

  // Auto-play video when slide changes
  useEffect(() => {
    if (slide?.type === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [current, slide]);

  // Kiosk mode: hide controls after inactivity
  useEffect(() => {
    if (!kioskMode) { setShowControls(true); return; }
    const showAndHide = () => {
      setShowControls(true);
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', showAndHide);
    window.addEventListener('touchstart', showAndHide);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    return () => {
      window.removeEventListener('mousemove', showAndHide);
      window.removeEventListener('touchstart', showAndHide);
      clearTimeout(controlsTimerRef.current);
    };
  }, [kioskMode]);

  const goTo = (dir) => {
    clearTimeout(timerRef.current);
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(prev => (prev + dir + total) % total);
      setTransitioning(false);
    }, 400);
  };

  const toggleKiosk = () => {
    setKioskMode(prev => !prev);
    if (!kioskMode) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[100]">
        <div className="text-center">
          <div className="h-10 w-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading fleet media...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-[100]" data-testid="virtual-tour">
      {/* Slide */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
        {slide?.type === 'video' ? (
          <video
            ref={videoRef}
            src={`${API}${slide.url}`}
            className="w-full h-full object-cover"
            muted={muted}
            playsInline
            onEnded={handleVideoEnd}
            data-testid="tour-video"
          />
        ) : slide?.type === 'image' ? (
          <img src={`${API}${slide.url}`} alt={slide.name} className="w-full h-full object-cover" data-testid="tour-image" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-gray-500 text-lg">{slide?.name}</p>
          </div>
        )}
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

      {/* Vehicle info overlay */}
      <div className={`absolute bottom-0 left-0 right-0 p-6 sm:p-10 transition-opacity duration-300 ${showControls || !kioskMode ? 'opacity-100' : 'opacity-0'}`}>
        {slide?.category && (
          <span className="text-xs font-bold text-red-500 tracking-widest uppercase mb-2 block">
            {CATEGORY_LABELS[slide.category] || slide.category}
          </span>
        )}
        <h2 className="text-2xl sm:text-4xl font-bold text-white mb-1" data-testid="tour-vehicle-name">{slide?.name}</h2>
        <div className="flex items-center gap-3 text-sm text-gray-400 mt-2">
          <span>{current + 1} of {total}</span>
          {slide?.type === 'video' && <span className="flex items-center gap-1"><Play className="h-3 w-3" /> Video</span>}
        </div>
      </div>

      {/* Top bar — branding + exit */}
      <div className={`absolute top-0 left-0 right-0 p-4 sm:p-6 flex items-center justify-between transition-opacity duration-300 ${showControls || !kioskMode ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center gap-3">
          <img src="/assets/ace_logo_horizontal.webp" alt="ACE" className="h-8 sm:h-10 opacity-80" />
          {kioskMode && (
            <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold hidden sm:inline">Virtual Tour</span>
          )}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
          data-testid="tour-exit-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Center controls */}
      <div className={`absolute inset-0 flex items-center justify-between px-4 sm:px-8 pointer-events-none transition-opacity duration-300 ${showControls || !kioskMode ? 'opacity-100' : 'opacity-0'}`}>
        <button
          onClick={() => goTo(-1)}
          className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors pointer-events-auto"
          data-testid="tour-prev-btn"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={() => goTo(1)}
          className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors pointer-events-auto"
          data-testid="tour-next-btn"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Bottom controls bar */}
      <div className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${showControls || !kioskMode ? 'opacity-100' : 'opacity-0'}`}>
        {/* Progress bar */}
        <div className="h-1 bg-white/10 mx-6 sm:mx-10 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-red-600 transition-all duration-300"
            style={{ width: `${((current + 1) / total) * 100}%` }}
          />
        </div>

        <div className="flex items-center justify-center gap-3 pb-6 sm:pb-8">
          {/* Play/Pause */}
          <button
            onClick={() => setPlaying(p => !p)}
            className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            data-testid="tour-play-pause"
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>

          {/* Mute toggle (for videos) */}
          <button
            onClick={() => setMuted(m => !m)}
            className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            data-testid="tour-mute-btn"
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>

          {/* Kiosk / Fullscreen toggle */}
          <button
            onClick={toggleKiosk}
            className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            data-testid="tour-kiosk-btn"
          >
            {kioskMode ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Slide indicator dots (non-kiosk, < 20 slides) */}
      {!kioskMode && total <= 20 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => { clearTimeout(timerRef.current); setCurrent(i); }}
              className={`h-1.5 rounded-full transition-all ${i === current ? 'w-6 bg-red-600' : 'w-1.5 bg-white/30 hover:bg-white/50'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VirtualTour;
